import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useClient } from "../context/ClientContext";
import { PageHeader, Section, Card, CardHeader, Pill, LoadingBar } from "../components/Primitives";
import {
  ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ZAxis, Cell
} from "recharts";

const SEV_TONE = { P0: "danger", P1: "warn", P2: "neutral" };

export default function DefectIntelligence() {
  const { activeClient, activeClientId } = useClient();
  const [defects, setDefects] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeClientId) return;
    setLoading(true);
    const params = { params: { client_id: activeClientId } };
    Promise.all([api.get("/defects", params), api.get("/defects/clusters", params)]).then(([a, b]) => {
      setDefects(a.data); setClusters(b.data); setLoading(false);
    });
  }, [activeClientId]);

  if (loading) return <LoadingBar label="Clustering defects" />;

  const scatterData = clusters.map((c, i) => ({
    name: c.cluster, x: i + 1, y: c.predictive_risk, z: c.count * 30, blast: c.blast_radius.length,
  }));

  return (
    <div data-testid="defect-intelligence-page">
      <PageHeader
        overline={`Layer 6 · ${activeClient?.name || ""}`}
        title="Defect Intelligence"
        description="AI-clustered defect patterns, predictive risk scoring, and downstream blast radius."
      />
      <Section>
        <div className="grid grid-cols-12 gap-6">
          <Card ai className="col-span-12 lg:col-span-7 p-6" testid="cluster-scatter-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline text-zinc-500 mb-1">AI cluster map</div>
                <div className="font-heading text-lg text-zinc-900">Predictive Risk × Cluster Volume</div>
              </div>
              <Pill tone="ai">{clusters.length} clusters</Pill>
            </div>
            <div className="h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 16, right: 24, bottom: 24, left: 16 }}>
                  <CartesianGrid stroke="#F4F4F5" />
                  <XAxis type="number" dataKey="x" name="cluster" hide />
                  <YAxis type="number" dataKey="y" name="risk" domain={[0, 100]} tick={{ fill: "#71717A", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} label={{ value: "Predictive Risk", angle: -90, position: "insideLeft", fill: "#71717A", fontSize: 11 }} />
                  <ZAxis type="number" dataKey="z" range={[60, 600]} />
                  <Tooltip
                    cursor={{ strokeDasharray: "3 3", stroke: "#A1A1AA" }}
                    contentStyle={{ background: "#0F172A", color: "#fff", border: "none", fontSize: 12 }}
                    formatter={(_, __, p) => p?.payload ? [`${p.payload.y} risk · ${p.payload.blast} downstream`, p.payload.name] : null}
                  />
                  <Scatter data={scatterData}>
                    {scatterData.map((d, i) => (
                      <Cell key={i} fill={d.y >= 80 ? "#E11D48" : d.y >= 60 ? "#F59E0B" : "#00AEEF"} />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="col-span-12 lg:col-span-5" testid="cluster-list-card">
            <CardHeader overline="Root-cause clusters" title="Patterns by Risk" />
            <div className="px-6 py-4 space-y-4">
              {clusters.map((c) => (
                <div key={c.cluster} className="border-b border-zinc-100 pb-4 last:border-0 last:pb-0" data-testid={`cluster-${c.cluster.replace(/\s+/g, "-")}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="text-sm font-medium text-zinc-900">{c.cluster}</div>
                    <Pill tone={c.predictive_risk >= 80 ? "danger" : c.predictive_risk >= 60 ? "warn" : "neutral"}>
                      {c.predictive_risk} risk
                    </Pill>
                  </div>
                  <div className="text-xs text-zinc-600 italic">"{c.root_cause}"</div>
                  <div className="mt-2 flex items-center gap-3 text-[11px] font-mono text-zinc-500">
                    <span>{c.count} defects</span>
                    <span>·</span>
                    <span>{c.workflows.length} workflow{c.workflows.length > 1 ? "s" : ""}</span>
                    <span>·</span>
                    <span className="text-rose-700">blast: {c.blast_radius.length}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {c.blast_radius.map((b) => (
                      <span key={b} className="inline-block text-[10px] px-1.5 py-0.5 bg-rose-50 text-rose-800 border border-rose-200 rounded-sm">{b}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="col-span-12" testid="defects-table-card">
            <CardHeader overline="All defects" title="Active Defect Inventory" />
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="defects-table">
                <thead>
                  <tr>
                    {["Key", "Title", "Workflow", "Severity", "Phase", "Cluster", "Predictive risk", "Owner"].map((h) => (
                      <th key={h} className="text-left px-6 py-3 overline text-zinc-500 border-b border-zinc-200">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {defects.map((d) => (
                    <tr key={d.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="px-6 py-3 font-mono text-xs text-zinc-700">{d.key}</td>
                      <td className="px-6 py-3 text-zinc-900">{d.title}</td>
                      <td className="px-6 py-3 text-zinc-600">{d.workflow}</td>
                      <td className="px-6 py-3"><Pill tone={SEV_TONE[d.severity] || "neutral"}>{d.severity}</Pill></td>
                      <td className="px-6 py-3 text-zinc-600 font-mono text-xs">{d.phase}</td>
                      <td className="px-6 py-3 text-zinc-700 text-xs">{d.cluster}</td>
                      <td className="px-6 py-3 font-mono">
                        <span className={d.predictive_risk >= 80 ? "text-rose-700" : d.predictive_risk >= 60 ? "text-amber-700" : "text-zinc-700"}>
                          {d.predictive_risk}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-zinc-600 text-xs">{d.owner}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </Section>
    </div>
  );
}
